import { COACH_NAME_MAX, validateCoachName } from '@goaltree/backend/convex/lib/coach'
import { cn } from '@goaltree/ui/lib/utils'
import { RotateCcwIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCoachIdentity, useSetCoachName } from '@/hooks/use-coach-name'

// "Coach name" input for the coach that's selected now. It shows the name in use
// (the default unless renamed), saves on blur/Enter, and the reset button clears
// this coach's name only. Typing the default name or clearing the field resets too.
export function CoachNameField({
  className,
  autoFocus,
  onDone,
}: {
  className?: string
  autoFocus?: boolean
  onDone?: () => void
}) {
  const { t } = useTranslation()
  const { loaded, customName, defaultName } = useCoachIdentity()
  const setName = useSetCoachName()
  const [draft, setDraft] = useState(customName ?? defaultName)
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (!focused) setDraft(customName ?? defaultName)
  }, [customName, defaultName, focused])

  const check = validateCoachName(draft)
  const error = check.ok
    ? null
    : check.error === 'tooLong'
      ? t('coach.nameField.tooLong', { max: COACH_NAME_MAX })
      : t('coach.nameField.invalid')

  function save() {
    if (!check.ok) return
    const name = check.name === defaultName ? null : (check.name ?? null)
    if (!name) setDraft(defaultName)
    if ((name ?? undefined) !== customName)
      void setName({ name }).catch(() => setDraft(customName ?? defaultName))
    onDone?.()
  }

  function reset() {
    setDraft(defaultName)
    void setName({ name: null })
    onDone?.()
  }

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false)
            save()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              e.currentTarget.blur()
            } else if (e.key === 'Escape') {
              e.stopPropagation()
              setDraft(customName ?? defaultName)
              onDone?.()
            }
          }}
          autoFocus={autoFocus}
          disabled={!loaded}
          placeholder={defaultName}
          aria-label={t('coach.nameField.label')}
          aria-invalid={!!error}
          maxLength={COACH_NAME_MAX * 4}
          className="h-9 min-w-0 flex-1 rounded-lg border border-input bg-transparent px-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50 aria-invalid:border-destructive dark:bg-input/30"
        />
        {customName && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={reset}
            title={t('coach.nameField.reset', { name: defaultName })}
            aria-label={t('coach.nameField.reset', { name: defaultName })}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <RotateCcwIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  )
}
